<template>
  <div class="participants-list">
    <!-- Participants Header -->
    <div class="participants-header">
      <h6 class="mb-0">
        <feather-icon icon="UsersIcon" size="16" />
        Participants
      </h6>
      <b-badge variant="primary" pill>
        {{ participants.length }}
      </b-badge>
    </div>

    <!-- Participants List -->
    <div class="participants-content">
      <div v-if="participants.length === 0" class="no-participants">
        <div class="text-center py-3">
          <feather-icon icon="UserIcon" size="24" class="text-muted" />
          <p class="text-muted mt-2 mb-0 small">
            Waiting for participants to join...
          </p>
        </div>
      </div>

      <div v-else class="participant-list">
        <div
          v-for="participant in sortedParticipants"
          :key="participant.id"
          class="participant-item"
          :class="{
            'current-user': participant.id === currentUserId,
            'teacher': participant.role === 'teacher',
            'student': participant.role === 'student'
          }"
        >
          <div class="participant-avatar">
            <div class="avatar-circle" :class="participant.role">
              {{ getInitials(participant.name) }}
            </div>
            <div
              class="status-indicator"
              :class="{
                'online': participant.status === 'online',
                'away': participant.status === 'away',
                'offline': participant.status === 'offline'
              }"
            ></div>
          </div>

          <div class="participant-info">
            <div class="participant-name">
              {{ participant.name }}
              <span v-if="participant.id === currentUserId" class="you-badge">
                (You)
              </span>
            </div>
            <div class="participant-role">
              <b-badge
                :variant="participant.role === 'teacher' ? 'primary' : 'success'"
                size="sm"
              >
                <feather-icon
                  :icon="participant.role === 'teacher' ? 'StarIcon' : 'UserIcon'"
                  size="10"
                />
                {{ participant.role }}
              </b-badge>
            </div>
          </div>

          <div class="participant-actions" v-if="showActions">
            <!-- Voice/Video indicators -->
            <div class="media-status">
              <div
                class="media-indicator"
                :class="{
                  'active': participant.hasAudio,
                  'muted': !participant.hasAudio
                }"
                :title="participant.hasAudio ? 'Microphone on' : 'Microphone off'"
              >
                <feather-icon
                  :icon="participant.hasAudio ? 'MicIcon' : 'MicOffIcon'"
                  size="12"
                />
              </div>
              <div
                class="media-indicator"
                :class="{
                  'active': participant.hasVideo,
                  'muted': !participant.hasVideo
                }"
                :title="participant.hasVideo ? 'Camera on' : 'Camera off'"
              >
                <feather-icon
                  :icon="participant.hasVideo ? 'VideoIcon' : 'VideoOffIcon'"
                  size="12"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ParticipantsList',
  props: {
    participants: {
      type: Array,
      default: () => []
    },
    currentUserId: {
      type: [String, Number],
      required: true
    },
    showActions: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    sortedParticipants() {
      // Sort participants: teachers first, then students, then by name
      return [...this.participants].sort((a, b) => {
        // Teachers first
        if (a.role === 'teacher' && b.role !== 'teacher') return -1
        if (b.role === 'teacher' && a.role !== 'teacher') return 1

        // Then current user
        if (a.id === this.currentUserId) return -1
        if (b.id === this.currentUserId) return 1

        // Then alphabetically by name
        return a.name.localeCompare(b.name)
      })
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return '?'

      const words = name.trim().split(' ')
      if (words.length === 1) {
        return words[0].charAt(0).toUpperCase()
      } else {
        return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
      }
    },

    getParticipantStatusColor(role) {
      return role === 'teacher' ? 'primary' : 'success'
    }
  }
}
</script>

<style lang="scss" scoped>
.participants-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  overflow: hidden;

  .participants-header {
    padding: 1rem;
    border-bottom: 1px solid #e3e3e3;
    background: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h6 {
      color: #2c3e50;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .participants-content {
    flex: 1;
    overflow-y: auto;

    .no-participants {
      padding: 1rem;
    }

    .participant-list {
      padding: 0.5rem 0;
    }

    .participant-item {
      display: flex;
      align-items: center;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #f5f5f5;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f8f9fa;
      }

      &.current-user {
        background-color: rgba(0, 123, 255, 0.05);
        border-left: 3px solid #007bff;
      }

      .participant-avatar {
        position: relative;
        margin-right: 0.75rem;

        .avatar-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.75rem;
          color: white;

          &.teacher {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }

          &.student {
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          }
        }

        .status-indicator {
          position: absolute;
          bottom: -1px;
          right: -1px;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid white;

          &.online {
            background-color: #28a745;
          }

          &.away {
            background-color: #ffc107;
          }

          &.offline {
            background-color: #6c757d;
          }
        }
      }

      .participant-info {
        flex: 1;
        min-width: 0; // Allow text to truncate

        .participant-name {
          font-weight: 500;
          font-size: 0.875rem;
          color: #2c3e50;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          .you-badge {
            font-weight: 400;
            color: #007bff;
            font-size: 0.75rem;
          }
        }

        .participant-role {
          margin-top: 0.125rem;
        }
      }

      .participant-actions {
        .media-status {
          display: flex;
          gap: 0.25rem;

          .media-indicator {
            width: 20px;
            height: 20px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.active {
              background-color: #28a745;
              color: white;
            }

            &.muted {
              background-color: #dc3545;
              color: white;
            }
          }
        }
      }
    }
  }
}

// Custom scrollbar for participants list
.participants-content::-webkit-scrollbar {
  width: 4px;
}

.participants-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.participants-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.participants-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Responsive adjustments
@media (max-width: 768px) {
  .participants-list {
    .participant-item {
      .participant-info .participant-name {
        font-size: 0.8rem;
      }
    }
  }
}
</style>